/* ──────────────────────────────────────────────────────────────
   Setup/Collections/index.tsx · Collections grid (live — /collections)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  ViewStyle,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import {
  Icon,
  Button,
  Chip,
  Input,
  Modal,
  Empty,
  Popover,
  MenuItem,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import {
  getCollections,
  getCollection,
  createCollection,
  updateCollection,
  deleteCollection,
  addCollectionItems,
  removeCollectionItem,
  reorderCollectionItems,
} from '../../services/catalog/taxonomy';
import { getItems } from '../../services/catalog/item';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import type { Collection } from '../../schemas/catalog/taxonomy';
import type { CatalogItem } from '../../schemas/catalog/item';

export default function CollectionsView() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [delTarget, setDelTarget] = useState<Collection | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCollections();
      setCollections(res.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load collections');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await createCollection({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      showSuccess(res, 'Collection created', `“${name.trim()}” created.`);
      setName(''); setDescription(''); setAddOpen(false);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not create collection');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = (c: Collection) => setDelTarget(c);

  // ── Edit collection (name + description) — persisted via PATCH /collections/:id ──
  const [editTarget, setEditTarget] = useState<Collection | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (c: Collection) => {
    setEditTarget(c);
    setEditName(c.name);
    setEditDesc((c as any).description ?? '');
  };

  const saveEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    setSavingEdit(true);
    try {
      await updateCollection(editTarget.id, { name: editName.trim(), description: editDesc.trim() });
      showSuccess({}, 'Collection updated', `“${editName.trim()}” saved.`);
      setEditTarget(null);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not update collection');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await deleteCollection(delTarget.id);
      showSuccess({}, 'Collection deleted', `“${delTarget.name}” removed.`);
      setCollections(prev => prev.filter(x => x.id !== delTarget.id));
      setDelTarget(null);
    } catch (err) {
      handleApiError(err, 'Could not delete collection');
    } finally {
      setDeleting(false);
    }
  };

  // ── Manage items + rename ──
  const [manage, setManage] = useState<Collection | null>(null);
  const [manageItems, setManageItems] = useState<any[]>([]);
  const [allItems, setAllItems] = useState<CatalogItem[]>([]);
  const [renameValue, setRenameValue] = useState('');

  const openManage = async (c: Collection) => {
    setManage(c); setRenameValue(c.name);
    try {
      const [detail, items] = await Promise.all([getCollection(c.id), getItems({ limit: 200 })]);
      setManageItems((detail as any).items ?? (detail as any).collectionItems ?? []);
      // Exclude deleted (ARCHIVED) items from the "add items" picker.
      setAllItems((items.items ?? []).filter((it: any) => it.currentState !== 'ARCHIVED'));
    } catch (err) { handleApiError(err, 'Could not open collection'); }
  };

  const refreshManage = async () => {
    if (!manage) return;
    try { const detail = await getCollection(manage.id); setManageItems((detail as any).items ?? (detail as any).collectionItems ?? []); }
    catch { /* ignore */ }
  };

  const addToCollection = async (itemId: string) => {
    if (!manage) return;
    try {
      await addCollectionItems(manage.id, { items: [{ catalogItemId: itemId }] });
      showSuccess({}, 'Item added', 'Added to collection.');
      await refreshManage();
    } catch (err) { handleApiError(err, 'Could not add item'); }
  };

  const removeFromCollection = async (itemId: string) => {
    if (!manage) return;
    try {
      await removeCollectionItem(manage.id, itemId);
      showSuccess({}, 'Item removed', 'Removed from collection.');
      await refreshManage();
    } catch (err) { handleApiError(err, 'Could not remove item'); }
  };

  const renameCollection = async () => {
    if (!manage || !renameValue.trim()) return;
    try {
      await updateCollection(manage.id, { name: renameValue.trim() });
      showSuccess({}, 'Renamed', 'Collection updated.');
      setManage(null);
      await load();
    } catch (err) { handleApiError(err, 'Could not rename'); }
  };

  const memberIds = new Set(manageItems.map((m: any) => m.catalogItemId ?? m.id));

  // Move a member up (-1) or down (+1): swap locally for instant feedback, then
  // persist DISTINCT sortOrder = new index for every member (0,1,2,…) so the
  // backend (which orders by sortOrder) keeps the exact order.
  const move = async (itemId: string, dir: -1 | 1) => {
    if (!manage) return;
    const idx = manageItems.findIndex((m: any) => (m.catalogItemId ?? m.id) === itemId);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= manageItems.length) return;
    const next = [...manageItems];
    [next[idx], next[j]] = [next[j], next[idx]];
    setManageItems(next); // optimistic — UI moves immediately
    try {
      await reorderCollectionItems(
        manage.id,
        next.map((m: any, i: number) => ({ catalogItemId: m.catalogItemId ?? m.id, sortOrder: i })),
      );
    } catch (err) {
      handleApiError(err, 'Could not reorder item');
      refreshManage();
    }
  };

  const cardBase: ViewStyle = {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    padding: 16,
    backgroundColor: colors.card,
    ...(shadows.sm as ViewStyle),
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <Text style={{ fontSize: fontSize['3xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans, letterSpacing: -0.02 * fontSize['3xl'] }}>
            Collections
          </Text>
          <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 4 }}>
            Curated groupings for merchandising — featured shelves, seasonal sets, gift guides.
          </Text>
        </View>
        <Button kind="accent" icon="plus" onPress={() => setAddOpen(true)}>New collection</Button>
      </View>

      <View style={{ maxWidth: 760, width: '100%' }}>
      {loading ? (
        <View style={{ paddingVertical: 48, alignItems: 'center' }}>
          <ActivityIndicator color={colors.accent} />
        </View>
      ) : collections.length === 0 ? (
        <Empty title="No collections yet">Create a curated grouping to feature items together.</Empty>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
          {collections.map(c => (
            <TouchableOpacity
              key={c.id}
              activeOpacity={0.85}
              onPress={() => openManage(c)}
              style={[cardBase, { minWidth: 220, flex: 1, ...Platform.select({ web: { cursor: 'pointer' } as ViewStyle, default: {} }) }]}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text style={{ fontSize: 28, marginBottom: 10 }}>🗂️</Text>
                {/* Isolated so tapping the menu doesn't open the manage view. */}
                <View onStartShouldSetResponder={() => true}>
                  <Popover
                    align="right"
                    width={150}
                    trigger={<Button kind="ghost" size="xs" iconOnly icon="more" aria-label="Collection actions" />}
                  >
                    {(close: () => void) => (
                      <>
                        <MenuItem icon="edit" onPress={() => { close(); openEdit(c); }}>Edit collection</MenuItem>
                        <MenuItem icon="box" onPress={() => { close(); openManage(c); }}>Manage items</MenuItem>
                        <MenuItem icon="trash" danger onPress={() => { close(); onDelete(c); }}>Delete</MenuItem>
                      </>
                    )}
                  </Popover>
                </View>
              </View>
              <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink, fontFamily: theme.font.sans, marginBottom: 4 }}>
                {c.name}
              </Text>
              {!!c.description && (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>{c.description}</Text>
              )}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                <Chip tone="info" icon={<Icon name="sparkles" size={11} color={colors.info} />}>Live</Chip>
                {c.item_count != null && <Chip tone="outline">{c.item_count} items</Chip>}
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}
      </View>

      {/* Manage items + rename */}
      <Modal
        open={!!manage}
        onClose={() => setManage(null)}
        size="wide"
        title={manage ? `Manage — ${manage.name}` : ''}
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setManage(null)}>Close</Button>
            <Button kind="accent" icon="check" disabled={!renameValue.trim()} onPress={renameCollection}>Rename</Button>
          </View>
        }
      >
        <View style={{ gap: 14 }}>
          <Input label="Collection name" value={renameValue} onChangeText={setRenameValue} />
          {/* Members — in collection order, reorderable */}
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans }}>
            In this collection ({manageItems.length})
          </Text>
          <View style={{ gap: 6 }}>
            {manageItems.length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>No items yet — add some below.</Text>
            ) : manageItems.map((m: any, i: number) => {
              const mid = m.catalogItemId ?? m.id;
              const label = allItems.find(a => a.id === mid)?.skuOrReference || (mid || '').slice(0, 8);
              return (
                <View key={mid} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 8, paddingHorizontal: 10 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink }}>{i + 1}. {label}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Button kind="ghost" size="xs" iconOnly icon="chev-u" aria-label="Move up" disabled={i === 0} onPress={() => move(mid, -1)} />
                    <Button kind="ghost" size="xs" iconOnly icon="chev-d" aria-label="Move down" disabled={i === manageItems.length - 1} onPress={() => move(mid, 1)} />
                    <Button kind="ghost" size="xs" icon="x" onPress={() => removeFromCollection(mid)}>Remove</Button>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Add items — everything not already a member */}
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans, marginTop: 6 }}>
            Add items
          </Text>
          <View style={{ maxHeight: 240, gap: 6 }}>
            {allItems.filter(it => !memberIds.has(it.id)).length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>All items are already in this collection.</Text>
            ) : allItems.filter(it => !memberIds.has(it.id)).map(it => (
              <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 8, paddingHorizontal: 10 }}>
                <Text style={{ fontFamily: theme.font.mono, fontSize: fontSize.sm, color: colors.ink }}>{it.skuOrReference || it.id.slice(0, 8)}</Text>
                <Button size="xs" icon="plus" onPress={() => addToCollection(it.id)}>Add</Button>
              </View>
            ))}
          </View>
        </View>
      </Modal>

      {/* Edit collection — name + description (PATCH /collections/:id) */}
      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title="Edit collection"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditTarget(null)}>Cancel</Button>
            <Button kind="accent" icon="check" disabled={!editName.trim() || savingEdit} onPress={saveEdit}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <Input label="Collection name" placeholder="e.g. Monsoon Menu" value={editName} onChangeText={setEditName} autoFocus />
          <Input label="Description" placeholder="Optional" value={editDesc} onChangeText={setEditDesc} />
        </View>
      </Modal>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="New collection"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOpen(false)}>Cancel</Button>
            <Button kind="accent" disabled={!name.trim() || busy} onPress={onCreate}>
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <Input label="Collection name" placeholder="e.g. Monsoon Menu" value={name} onChangeText={setName} autoFocus />
          <Input label="Description" placeholder="Optional" value={description} onChangeText={setDescription} />
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!delTarget}
        title="Delete collection?"
        message={delTarget ? `"${delTarget.name}" will be deleted. (Items themselves are not deleted.)` : ''}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDelTarget(null)}
        onClose={() => setDelTarget(null)}
      />
    </ScrollView>
  );
}
